import api from '../axiosIntercepture';

function normalizeListResponse(body) {
  if (!body) return { rows: [], total: 0 };
  const inner = body.data !== undefined ? body.data : body;

  if (Array.isArray(inner)) {
    return { rows: inner, total: body.total ?? body.count ?? inner.length };
  }
  const arrayKeys = ['data', 'results', 'items', 'records', 'list', 'rows', 'docs'];
  for (const key of arrayKeys) {
    if (Array.isArray(inner?.[key])) {
      return {
        rows: inner[key],
        total: inner.total ?? inner.count ?? inner.totalCount ?? body.total ?? inner[key].length,
      };
    }
  }
  return { rows: [], total: 0 };
}

/** Pull a human-readable error from UAT / partner-portal style error payloads. */
function normalizeApiErrorMessage(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || '';
  if (typeof d === 'string') {
    const pre = d.match(/<pre>([^<]*)<\/pre>/i);
    if (pre?.[1]) return pre[1].trim();
    if (d.includes('Cannot POST') || d.includes('Cannot GET')) {
      const oneLine = d.replace(/\s+/g, ' ').trim();
      return oneLine.length > 240 ? `${oneLine.slice(0, 240)}…` : oneLine;
    }
    return d.length > 400 ? `${d.slice(0, 400)}…` : d;
  }
  if (typeof d.message === 'string' && d.message) return d.message;
  if (typeof d.error === 'string' && d.error) return d.error;
  if (d.error && typeof d.error === 'object' && d.error.message) return String(d.error.message);
  if (Array.isArray(d.errors)) {
    return d.errors
      .map((e) => (typeof e === 'string' ? e : e?.msg || e?.message || JSON.stringify(e)))
      .filter(Boolean)
      .join('; ');
  }
  return err?.message || 'Request failed';
}

function isMongoLikeId(s) {
  if (s == null || typeof s !== 'string') return false;
  const t = s.trim();
  return /^[a-f\d]{24}$/i.test(t);
}

/**
 * Unwrap nested API envelopes. Skips `data: null` so we do not collapse a valid parent object.
 */
function unwrapGatePassEntity(raw) {
  if (raw == null) return raw;
  if (Array.isArray(raw) && raw.length === 1) {
    return unwrapGatePassEntity(raw[0]);
  }
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw !== 'object') return raw;

  if (raw._id != null || raw.id != null) {
    return raw;
  }

  const keys = ['data', 'result', 'gatePass', 'gate_pass', 'document', 'record', 'payload'];
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
    const nested = raw[key];
    if (nested == null) continue;
    if (nested === raw) continue;
    return unwrapGatePassEntity(nested);
  }
  return raw;
}

/** Normalize create/get/update response to a document the UI can use. */
function coerceGatePassDoc(raw) {
  const entity = unwrapGatePassEntity(raw);
  if (entity == null) return entity;
  if (typeof entity === 'string' && entity.trim()) {
    const id = entity.trim();
    return { _id: id, id };
  }
  return entity;
}

/** Best-effort id extraction for UAT responses that omit standard `_id` placement. */
function scanObjectForGatePassId(obj, depth = 0) {
  if (obj == null || depth > 6) return '';
  if (typeof obj === 'string' && isMongoLikeId(obj)) return obj.trim();
  if (typeof obj !== 'object') return '';

  const direct = extractGatePassId(obj);
  if (direct) return direct;

  const keyHints = [
    'insertedId',
    'inserted_id',
    'newId',
    'mongoId',
    'gate_pass_id',
    'gatePassId',
    'resourceId',
    '_id',
    'id',
  ];
  for (const k of keyHints) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
    const v = obj[k];
    if (v == null) continue;
    if (typeof v === 'object' && v.$oid) return String(v.$oid);
    if (typeof v === 'string' && isMongoLikeId(v)) return v.trim();
  }

  for (const v of Object.values(obj)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const found = scanObjectForGatePassId(v, depth + 1);
      if (found) return found;
    }
    if (Array.isArray(v) && v.length) {
      const found = scanObjectForGatePassId(v[0], depth + 1);
      if (found) return found;
    }
  }
  return '';
}

function idFromLocationHeader(response) {
  const loc = response?.headers?.location || response?.headers?.Location;
  if (!loc) return '';
  const m = String(loc).match(/([a-f0-9]{24})/i);
  return m ? m[1] : '';
}

/** Treat API placeholders as empty approver text. */
function isPlaceholderApproverValue(val) {
  if (val == null) return true;
  const t = String(val).trim().toLowerCase();
  return (
    !t ||
    t === 'null' ||
    t === 'undefined' ||
    t === 'nan' ||
    t === 'nil' ||
    t === '—' ||
    t === '-'
  );
}

/** Build display name from a populated user / approver object. */
function labelFromUserLikeObject(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return '';
  const fn = String(obj.firstName ?? obj.first_name ?? '').trim();
  const ln = String(obj.lastName ?? obj.last_name ?? '').trim();
  const full = [fn, ln].filter(Boolean).join(' ').trim();
  const direct =
    full ||
    String(obj.fullName ?? obj.full_name ?? '').trim() ||
    String(obj.displayName ?? obj.display_name ?? '').trim() ||
    String(obj.userName ?? obj.username ?? obj.name ?? '').trim() ||
    String(obj.email ?? '').trim() ||
    '';
  if (direct) return direct;
  const nestKeys = ['user', 'userId', 'profile', 'account'];
  for (const nk of nestKeys) {
    const sub = obj[nk];
    if (sub && typeof sub === 'object' && !Array.isArray(sub)) {
      const nested = labelFromUserLikeObject(sub);
      if (nested) return nested;
    }
  }
  return '';
}

function readApproverScalar(val) {
  if (val == null) return '';
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      for (const el of val) {
        const s = readApproverScalar(el);
        if (s) return s;
      }
      return '';
    }
    if (val.$oid != null) return '';
    if (val instanceof Date) return '';
    return labelFromUserLikeObject(val);
  }
  const s = String(val).trim();
  return isPlaceholderApproverValue(s) ? '' : s;
}

/** Deep scan for approver-like keys (nested audit / DTO shapes). */
const APPROVER_KEY_RE =
  /^(approved_by|approvedBy|approved_by_name|approvedByName|approver_name|approverName|approved_user|approvedUser|approved_username|approvedUsername)$/i;

function deepPickApprovedByFromDoc(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 8) return '';
  for (const [k, v] of Object.entries(obj)) {
    if (APPROVER_KEY_RE.test(k)) {
      const s = readApproverScalar(v);
      if (s) return s;
    }
  }
  for (const v of Object.values(obj)) {
    if (v == null || typeof v !== 'object') continue;
    if (Array.isArray(v)) {
      for (const el of v) {
        if (el && typeof el === 'object') {
          const s = deepPickApprovedByFromDoc(el, depth + 1);
          if (s) return s;
        }
      }
    } else {
      const s = deepPickApprovedByFromDoc(v, depth + 1);
      if (s) return s;
    }
  }
  return '';
}

/** Read approver display string from various API / ORM field names. */
function pickApprovedByFromDoc(doc) {
  if (!doc || typeof doc !== 'object') return '';
  const scalarKeys = [
    'approved_by',
    'approvedBy',
    'approved_by_name',
    'approvedByName',
    'approver_name',
    'approverName',
    'approved_user',
    'approvedUser',
    'approved_username',
    'approvedUsername',
  ];
  for (const k of scalarKeys) {
    if (!Object.prototype.hasOwnProperty.call(doc, k)) continue;
    const s = readApproverScalar(doc[k]);
    if (s) return s;
  }
  const nestedKeys = ['approver', 'approvedByUser', 'approved_by_user', 'approvedUser'];
  for (const k of nestedKeys) {
    if (!Object.prototype.hasOwnProperty.call(doc, k)) continue;
    const s = readApproverScalar(doc[k]);
    if (s) return s;
  }
  return deepPickApprovedByFromDoc(doc);
}

/** Canonical approver label for UI and API payloads after load / write. */
export function resolveApprovedByDisplay(doc) {
  return pickApprovedByFromDoc(doc);
}

/** Ensure gate pass documents always expose a string `approved_by` for the UI. */
function normalizeGatePassDoc(doc) {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return doc;
  const approved_by = pickApprovedByFromDoc(doc);
  return {
    ...doc,
    approved_by,
    approvedBy: approved_by,
  };
}

/**
 * Merge request payload with API response so the UI always has fields + `_id` when possible.
 * Does not throw when HTTP succeeded but the body shape is minimal.
 */
function mergeGatePassWriteResponse(payload, response) {
  const body = response?.data;
  const doc = coerceGatePassDoc(body);
  const baseDoc =
    doc && typeof doc === 'object' && !Array.isArray(doc) ? doc : {};

  let id = extractGatePassId(baseDoc);
  if (!id) id = scanObjectForGatePassId(body);
  if (!id) id = idFromLocationHeader(response);

  const merged = {
    ...payload,
    ...baseDoc,
    items:
      Array.isArray(baseDoc.items) && baseDoc.items.length > 0
        ? baseDoc.items
        : Array.isArray(payload?.items)
          ? payload.items
          : [],
  };
  if (id) {
    merged._id = id;
    merged.id = id;
  }
  const sentApprover = pickApprovedByFromDoc(payload);
  const resolvedApprover = pickApprovedByFromDoc(merged) || sentApprover;
  if (resolvedApprover) {
    merged.approved_by = resolvedApprover;
    merged.approvedBy = resolvedApprover;
  }
  return merged;
}

/** Attach approver label when API omitted it but the client knows who approved. */
function withApproverLabel(doc, approverLabel) {
  if (!doc || typeof doc !== 'object') return doc;
  const label = approverLabel != null ? String(approverLabel).trim() : '';
  const existing = pickApprovedByFromDoc(doc);
  const approved_by = existing || label;
  if (!approved_by) return normalizeGatePassDoc(doc);
  return normalizeGatePassDoc({
    ...doc,
    approved_by,
    approvedBy: approved_by,
  });
}

/** Resolve Mongo / string id from various API shapes (aligned with Stock Transfer Out helpers). */
export function extractGatePassId(r) {
  if (!r || typeof r !== 'object') return '';
  const candidates = [
    r._id,
    r.id,
    r.gatePassId,
    r.gate_pass_id,
    r.recordId,
    r.record_id,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && c.$oid) return String(c.$oid);
  }
  return '';
}

export function mapGatePassListRow(doc) {
  if (!doc) return null;
  const id =
    extractGatePassId(doc) ||
    String(doc.gate_pass_no ?? doc.gatePassNo ?? '')
      .trim()
      .replace(/\s+/g, '-');
  if (!id) return null;
  return {
    id,
    _id: id,
    gate_pass_no: doc.gate_pass_no ?? doc.gatePassNo ?? '—',
    gate_pass_date: doc.gate_pass_date ?? doc.gatePassDate ?? '—',
    warehouse: doc.warehouse_label ?? doc.warehouse_id ?? '—',
    vendor_name: doc.vendor_name ?? '—',
    vehicle_no: doc.vehicle_no ?? doc.vehicleNo ?? '—',
    status: doc.status ?? '—',
    created_at: doc.created_at ?? doc.createdAt ?? '—',
  };
}

/**
 * Fetch gate passes from DB: `GET /api/gate-pass?page=&limit=` (REST).
 * If the server only exposes listing on the same path via POST (common on some local stacks),
 * falls back to `POST /api/gate-pass` with JSON `{ page, limit }` — same path as create, different body.
 */
export async function fetchGatePassList({ page = 1, limit = 10 } = {}) {
  const parseList = (response) => {
    const { rows, total } = normalizeListResponse(response.data);
    return { rows: rows.map(mapGatePassListRow).filter(Boolean), total };
  };

  try {
    try {
      const response = await api.get('gate-pass', {
        params: { page, limit },
      });
      return parseList(response);
    } catch (getErr) {
      const st = getErr?.response?.status;
      const msg = normalizeApiErrorMessage(getErr);
      const tryPost =
        st === 404 ||
        st === 405 ||
        /cannot\s+get/i.test(msg) ||
        /method\s+not\s+allowed/i.test(msg);
      if (!tryPost) throw getErr;
      const response = await api.post('gate-pass', { page, limit });
      return parseList(response);
    }
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to fetch gate pass list');
  }
}

/**
 * GET /api/gate-pass/:mongoId
 */
export async function getGatePassById(id) {
  if (!id) throw new Error('Missing gate pass id');
  try {
    const response = await api.get(`gate-pass/${encodeURIComponent(String(id))}`);
    const body = response.data;
    if (body?.success === false) {
      throw new Error(body?.message || 'Failed to load gate pass');
    }
    return normalizeGatePassDoc(coerceGatePassDoc(body));
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to load gate pass');
  }
}

async function enrichCreatedDocWithListId(mergedDoc) {
  if (!mergedDoc || typeof mergedDoc !== 'object' || extractGatePassId(mergedDoc)) {
    return mergedDoc;
  }
  const refNo = String(mergedDoc.gate_pass_no ?? mergedDoc.gatePassNo ?? '').trim();
  if (!refNo) return mergedDoc;
  try {
    const { rows } = await fetchGatePassList({ page: 1, limit: 100 });
    const hit = rows.find((r) => String(r?.gate_pass_no ?? '').trim() === refNo);
    if (hit && extractGatePassId(hit)) {
      return {
        ...mergedDoc,
        _id: hit._id,
        id: hit.id,
      };
    }
  } catch {
    /* ignore */
  }
  return mergedDoc;
}

/**
 * POST /api/gate-pass
 */
export async function createGatePass(payload) {
  try {
    const response = await api.post('gate-pass', payload);
    const body = response.data;
    if (body?.success === false) {
      throw new Error(body?.message || 'Failed to create gate pass');
    }
    const merged = mergeGatePassWriteResponse(payload, response);
    const withId = await enrichCreatedDocWithListId(merged);
    return normalizeGatePassDoc(withId);
  } catch (err) {
    if (err instanceof Error && err.message && err.response == null) {
      throw err;
    }
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to create gate pass');
  }
}

/**
 * PUT /api/gate-pass/:mongoId
 */
export async function updateGatePass(id, payload) {
  if (!id) throw new Error('Missing gate pass id');
  try {
    const response = await api.put(
      `gate-pass/${encodeURIComponent(String(id))}`,
      payload
    );
    const body = response.data;
    if (body?.success === false) {
      throw new Error(body?.message || 'Failed to update gate pass');
    }
    const merged = mergeGatePassWriteResponse(payload, response);
    if (!extractGatePassId(merged)) {
      merged._id = String(id).trim();
      merged.id = String(id).trim();
    }
    return normalizeGatePassDoc(merged);
  } catch (err) {
    if (err instanceof Error && err.message && err.response == null) {
      throw err;
    }
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to update gate pass');
  }
}

/**
 * DELETE /api/gate-pass/:mongoId
 */
export async function deleteGatePass(id) {
  if (!id) throw new Error('Missing gate pass id');
  try {
    const response = await api.delete(`gate-pass/${encodeURIComponent(String(id))}`);
    const body = response.data;
    if (body?.success === false) {
      throw new Error(body?.message || 'Failed to delete gate pass');
    }
    return coerceGatePassDoc(body) ?? body;
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to delete gate pass');
  }
}

/**
 * Approve gate pass.
 * 1) `POST /api/gate-pass/:id/approve` with `{ approved_by }` (dedicated approve API)
 * 2) `PUT /api/gate-pass/:id` with full document when provided (persists all fields)
 * Refetches by id and ensures `approved_by` is present for the UI.
 */
export async function approveGatePass(id, { approved_by: approverLabel, document: documentPayload } = {}) {
  if (!id) throw new Error('Missing gate pass id');
  const label = approverLabel != null ? String(approverLabel).trim() : '';
  if (!label) throw new Error('Missing approver (approved_by)');
  const idEnc = encodeURIComponent(String(id));
  const approveBody = { approved_by: label, approvedBy: label };
  const putBody =
    documentPayload && typeof documentPayload === 'object'
      ? { ...documentPayload, approved_by: label, approvedBy: label, status: 'Approved' }
      : { ...approveBody, status: 'Approved' };
  const baseMerge = {
    ...(documentPayload && typeof documentPayload === 'object' ? documentPayload : {}),
    _id: String(id).trim(),
    id: String(id).trim(),
    approved_by: label,
    approvedBy: label,
    status: 'Approved',
  };

  const shouldTryAlternateRoute = (err) => {
    const st = err?.response?.status;
    const msg = normalizeApiErrorMessage(err);
    return (
      st === 404 ||
      st === 405 ||
      /cannot\s+(post|put)/i.test(msg) ||
      /method\s+not\s+allowed/i.test(msg)
    );
  };

  try {
    let response;
    let approveSucceeded = false;
    try {
      response = await api.post(`gate-pass/${idEnc}/approve`, approveBody);
      approveSucceeded = true;
    } catch (postErr) {
      if (!shouldTryAlternateRoute(postErr)) throw postErr;
    }
    if (!approveSucceeded) {
      try {
        response = await api.put(`gate-pass/${idEnc}`, putBody);
      } catch (putErr) {
        if (!shouldTryAlternateRoute(putErr)) throw putErr;
        response = await api.post(`gate-pass/${idEnc}/approve`, approveBody);
      }
    } else if (documentPayload && typeof documentPayload === 'object') {
      try {
        const putResponse = await api.put(`gate-pass/${idEnc}`, putBody);
        response = putResponse;
      } catch {
        /* approve succeeded; PUT optional for persisting full record */
      }
    }
    const body = response?.data;
    if (body?.success === false) {
      throw new Error(body?.message || 'Failed to approve gate pass');
    }
    let merged = withApproverLabel(
      normalizeGatePassDoc(mergeGatePassWriteResponse(baseMerge, response)),
      label
    );
    try {
      const fresh = await getGatePassById(id);
      merged = withApproverLabel(fresh, label);
    } catch {
      /* keep merge from approve response */
    }
    return merged;
  } catch (err) {
    throw new Error(normalizeApiErrorMessage(err) || 'Failed to approve gate pass');
  }
}
